import { useState } from "react";
import { useForm } from "react-hook-form";
import Form from "../../../components/Form";
import DynamicFormRenderer from "../../../components/DynamicFormRenderer";
import { icclrFormConfig } from "./formConfig";
import Api from "../../../Api";

const ICCLRForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    unregister,
    reset,
    control,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {},
    shouldFocusError: true,
    criteriaMode: "all",
  });

  const submitForm = async (data) => {
    try {
      setLoading(true);

      const { fieldMapping, autoFields } = icclrFormConfig.metadata;
      const today = new Date();

      // Transform value based on type
      const transformValue = (value, transform) => {
        if (transform === "selectArray") {
          return [parseInt(value)];
        }
        if (transform === "checkboxArray") {
          return (Array.isArray(value) ? value : []).map((v) => parseInt(v));
        }
        return value;
      };

      // Build aanmeldigen payload (4471)
      const aanmeldigen = { defDossier: icclrFormConfig.metadata.defDossier };
      for (const [formField, mapping] of Object.entries(fieldMapping)) {
        if (mapping.aanmeldigen && data[formField] !== undefined) {
          aanmeldigen[mapping.aanmeldigen] = mapping.transform
            ? transformValue(data[formField], mapping.transform)
            : data[formField];
        }
      }
      // Auto fields
      for (const [fieldId, value] of Object.entries(autoFields.aanmeldigen)) {
        aanmeldigen[fieldId] = value === "today" ? today.toISOString() : value;
      }

      // Build personen payload (4472)
      const personen = { defDossier: icclrFormConfig.metadata.linkedDossier };
      for (const [formField, mapping] of Object.entries(fieldMapping)) {
        if (mapping.personen && data[formField] !== undefined) {
          personen[mapping.personen] = mapping.transform
            ? transformValue(data[formField], mapping.transform)
            : data[formField];
        }
      }
      // Auto fields
      for (const [fieldId, value] of Object.entries(autoFields.personen)) {
        personen[fieldId] = value === "today" ? today.toISOString() : value;
      }

      // Create both dossiers
      const resultAanmeldigen = await Api.dossiers.Create(aanmeldigen);
      const resultPerson = await Api.dossiers.Create(personen);

      // Link personen to aanmeldigen
      if (resultAanmeldigen && resultPerson) {
        const linked = {
          [icclrFormConfig.metadata.linkFieldId]: [resultPerson.id],
        };
        const queryParams = {
          displayPosition: icclrFormConfig.metadata.linkDisplayPosition,
        };
        await Api.dossiers.Put(resultAanmeldigen.id, queryParams, linked);
      }

      setLoading(false);
      setShowModal(true);
    } catch (error) {
      console.error("Form submission error:", error);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center h-screen fixed w-full z-50">
          <div className="bg-slate-500 absolute w-full h-full opacity-50"></div>
          <div className="relative">
            <div className="h-24 w-24 rounded-full border-t-8 border-b-8 border-slate-200"></div>
            <div className="absolute top-0 left-0 h-24 w-24 rounded-full border-t-8 border-b-8 border-slate-300 animate-spin"></div>
          </div>
        </div>
      )}

      {showModal && (
        <div
          id="successModal"
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-hidden="true"
        >
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-50"
              onClick={() => setShowModal(false)}
            ></div>
            <div className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-auto">
              <button
                onClick={() => setShowModal(false)}
                type="button"
                className="text-gray-400 absolute top-2.5 right-2.5 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                aria-label="Close modal"
              >
                <svg
                  aria-hidden="true"
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </button>
              <div className="w-12 h-12 rounded-full bg-green-100 p-2 flex items-center justify-center mx-auto mb-3.5">
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </div>
              <p className="mb-4 text-lg font-semibold text-gray-900">
                Successfully!
              </p>
              <button
                onClick={() => {
                  setShowModal(false);
                  reset();
                }}
                type="button"
                className="py-2 px-3 text-sm font-medium text-center text-white rounded-lg bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 w-full"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="py-5 px-4 sm:px-10 lg:px-36 d-flex flex-column gap-5">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 py-6 px-4 sm:px-8 lg:px-10">
          <Form
            {...props}
            register={register}
            handleSubmit={handleSubmit}
            setValue={setValue}
            unregister={unregister}
            formState={{ errors }}
            onSubmit={submitForm}
            defDossier={icclrFormConfig.metadata.defDossier}
            reset={reset}
            skipLinkedContainers={true}
          >
            <DynamicFormRenderer
              config={icclrFormConfig}
              register={register}
              errors={errors}
              setValue={setValue}
              unregister={unregister}
              control={control}
              watch={watch}
            />
          </Form>
        </div>
      </div>
    </>
  );
};

export default ICCLRForm;
